/**
 * Preview spots (q-dungeon-layout-generator-build S4) — WHERE the explorer's
 * walker stands for a named look at a generated interior.
 *
 * S4 has to show the owner's four adjectives (moody, condensed, directed,
 * beatable) at an entrance, a choke, a rest and the boss hall, but the explorer
 * always arrives at the entrance and the lane has no movement DSL. This is the
 * weakest thing that answers it: one pure function from a v2 plan to a walker
 * pose, read by the dev hook only (`__GC_DEVDUNGEON`, dev builds). Nothing here
 * runs in normal play.
 *
 * Poses are in the interior's LOCAL frame (plan-local metres, the frame the
 * layout and the collision share); the explorer adds its own offset. A spot the
 * plan does not have (a v1 plan, or no `rest` room) is `null`, never a stand-in
 * — a capture labelled "rest" of some other chamber is exactly the confident
 * wrong answer the lane exists to refuse.
 */

import type { DungeonPlan, DungeonPoint } from '@great-cloud/procedural-dungeons';

export const PREVIEW_SPOTS = ['entrance', 'choke', 'rest', 'boss'] as const;
export type PreviewSpot = (typeof PREVIEW_SPOTS)[number];

/** `yaw` uses the explorer's convention: forward = (sin yaw, cos yaw). */
export interface PreviewSpotPose {
  x: number;
  z: number;
  yaw: number;
}

/** How far back from a chamber's centre the walker stands, as a share of its inscribed radius. */
const CHAMBER_STANDBACK = 0.55;

export function isPreviewSpot(value: unknown): value is PreviewSpot {
  return typeof value === 'string' && (PREVIEW_SPOTS as readonly string[]).includes(value);
}

/** Upper bound on a dev re-roll cycle — a sampling knob, not a search. */
export const MAX_PREVIEW_CYCLE = 64;

export interface DevDungeonOptions {
  cycle?: number;
  at?: PreviewSpot;
  /**
   * Presentation mode for capture footage: no dev banner, no walker proxy (the capsule is
   * never public-facing), and a slow forward walk so the clip moves through the rooms.
   */
  present?: boolean;
}

/**
 * Validate `__GC_DEVDUNGEON`'s second argument. `undefined` ⇒ `{}` (the
 * one-argument call is unchanged); any unknown key, a non-integer or
 * out-of-range `cycle`, or an unknown `at` ⇒ null, which the hook turns into
 * `false` — a lane typo must fail loudly, not capture the entrance.
 */
export function parseDevDungeonOptions(raw: unknown): DevDungeonOptions | null {
  if (raw === undefined) return {};
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  if (Object.keys(record).some((key) => key !== 'cycle' && key !== 'at' && key !== 'present'))
    return null;
  const out: DevDungeonOptions = {};
  if (record.cycle !== undefined) {
    const cycle = record.cycle;
    if (typeof cycle !== 'number' || !Number.isInteger(cycle)) return null;
    if (cycle < 0 || cycle > MAX_PREVIEW_CYCLE) return null;
    out.cycle = cycle;
  }
  if (record.at !== undefined) {
    if (!isPreviewSpot(record.at)) return null;
    out.at = record.at;
  }
  if (record.present !== undefined) {
    if (typeof record.present !== 'boolean') return null;
    out.present = record.present;
  }
  return out;
}

function yawAlong(from: DungeonPoint, to: DungeonPoint): number {
  return Math.atan2(to.x - from.x, to.z - from.z);
}

/**
 * The pose for `spot`, or null when the plan has no such place.
 *
 * - `entrance`: the entrance chamber's centre (where `enter` already puts the
 *   walker), facing the layout's centre.
 * - `rest` / `boss`: the first room of that role; the walker stands back from
 *   the chamber centre on the side facing the entrance, looking in, so the
 *   frame holds the chamber rather than the wall behind it.
 * - `choke`: the midpoint of the first choke corridor segment, facing along it.
 */
export function previewSpotPose(plan: DungeonPlan, spot: PreviewSpot): PreviewSpotPose | null {
  const layout = plan.layout;
  if (!layout) return null;
  const chamberOf = (role: string) => {
    const room = plan.rooms.find((entry) => entry.role === role);
    return room ? layout.chambers.find((chamber) => chamber.roomId === room.id) : undefined;
  };
  const entrance = chamberOf('entrance');
  if (!entrance) return null;

  if (spot === 'entrance') {
    const mid = {
      x: (layout.bounds.minX + layout.bounds.maxX) / 2,
      z: (layout.bounds.minZ + layout.bounds.maxZ) / 2,
    };
    const same = mid.x === entrance.centre.x && mid.z === entrance.centre.z;
    return {
      x: entrance.centre.x,
      z: entrance.centre.z,
      yaw: same ? 0 : yawAlong(entrance.centre, mid),
    };
  }

  if (spot === 'choke') {
    for (const corridor of layout.corridors) {
      const segment = corridor.segments.find((entry) => entry.kind === 'choke');
      if (!segment) continue;
      return {
        x: (segment.from.x + segment.to.x) / 2,
        z: (segment.from.z + segment.to.z) / 2,
        yaw: yawAlong(segment.from, segment.to),
      };
    }
    return null;
  }

  const target = chamberOf(spot);
  if (!target) return null;
  const dx = target.centre.x - entrance.centre.x;
  const dz = target.centre.z - entrance.centre.z;
  const length = Math.hypot(dx, dz);
  // The entrance IS the target only in a degenerate plan; look along +z then.
  const ux = length > 0 ? dx / length : 0;
  const uz = length > 0 ? dz / length : 1;
  const back = target.inscribedRadius * CHAMBER_STANDBACK;
  return {
    x: target.centre.x - ux * back,
    z: target.centre.z - uz * back,
    yaw: Math.atan2(ux, uz),
  };
}
